import { useState } from 'react';
import { Box, List, ListItem, ListItemIcon, ListItemText, Collapse, IconButton, Tooltip, Avatar, Typography, Divider, Chip } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LockIcon from '@mui/icons-material/Lock';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../../contexts/AuthContext';
import UserSelectorModal from '../shared/UserSelectorModal';
import ClinicSelector from '../shared/ClinicSelector';
import { safeLocalStorage } from '../../utils/storageUtils';

// Styled components for custom appearance
const NavList = styled(List)({
  padding: '40px 0 0 0',
  marginTop: 0,
  position: 'relative',
  zIndex: 1000,
});

const NavItem = styled(ListItem)(({ theme, active, expanded, highlight }) => ({
  padding: expanded ? '10px 16px' : '10px 0',
  margin: '4px 0',
  justifyContent: expanded ? 'flex-start' : 'center',
  color: active ? theme.palette.navActive.main : 'white',
  backgroundColor: highlight ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
  '&:hover': {
    backgroundColor: highlight ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.1)',
  },
  borderRadius: 0,
  position: 'relative',
  borderLeft: highlight ? `3px solid ${theme.palette.secondary.main}` : 'none',
}));

const NavItemText = styled(ListItemText)({
  margin: 0,
  '& .MuiTypography-root': {
    fontWeight: 500,
  },
});

const SubMenu = styled(Collapse)({
  '& .MuiList-root': {
    padding: '0 0 0 16px',
  },
});

const SubMenuItem = styled(ListItem)(({ theme, active }) => ({
  padding: '8px 16px',
  color: active ? theme.palette.navActive.main : 'rgba(255, 255, 255, 0.85)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: 'white',
  },
}));

const SideNav = ({ items, onToggleExpand }) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const [openSubMenus, setOpenSubMenus] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, logout, refreshProfile, isAdmin, isProvider, isSponsor } = useAuth();
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectorUserType, setSelectorUserType] = useState(null);

  const isItemAccessible = (item) => {
    if (!item.requiresRole) return true;
    const userRole = profile?.tenant_role;
    if (item.requiresRole === 'admin' || item.requiresRole === 'super_admin') return isAdmin;
    if (item.requiresRole === 'physician' || item.requiresRole === 'provider') return isProvider;
    if (item.requiresRole === 'sponsor') return isSponsor;
    return userRole === item.requiresRole;
  };

  const toggleExpand = () => {
    const newExpandedState = !expanded;
    setExpanded(newExpandedState);
    if (!newExpandedState) {
      setOpenSubMenus({});
    }
    if (onToggleExpand) {
      onToggleExpand(newExpandedState);
    }
  };

  const handleNavItemClick = (item) => {
    if (item.needsSelection) {
      const typeMap = {
        'patient-dashboard': 'patient',
        'sponsor-dashboard': 'sponsor',
        'clinic-staff-dashboard': 'clinic_staff',
      };
      setSelectorUserType(typeMap[item.id] || 'patient');
      setSelectorOpen(true);
    } else {
      if (expanded) {
        toggleExpand();
      }
      // Save last visited page for persistence (with error handling)
      if (item.path && item.path !== '#' && !item.path.startsWith('#')) {
        safeLocalStorage.setItem('lastVisitedPage', item.path);
        // Also save to recent pages history
        saveRecentPage(item.path);
      }
      navigate(item.path);
    }
  };

  // Save to recent pages history (last 10 pages)
  const saveRecentPage = (path) => {
    try {
      const recent = getRecentPages();
      const updated = [path, ...recent.filter(p => p !== path)].slice(0, 10);
      safeLocalStorage.setItem('recentPages', JSON.stringify(updated));
    } catch (error) {
      // Silently fail if storage is unavailable
      if (import.meta.env.DEV) {
        console.warn('Failed to save recent page:', error);
      }
    }
  };

  // Get recent pages from storage
  const getRecentPages = () => {
    try {
      const recent = safeLocalStorage.getItem('recentPages');
      return recent ? JSON.parse(recent) : [];
    } catch (error) {
      return [];
    }
  };

  const handleSubMenuItemClick = (childPath) => {
    if (expanded) {
      toggleExpand();
    }
    // Save last visited page for persistence (with error handling)
    if (childPath && childPath !== '#' && !childPath.startsWith('#')) {
      safeLocalStorage.setItem('lastVisitedPage', childPath);
      // Also save to recent pages history
      saveRecentPage(childPath);
    }
  };

  const toggleSubMenu = (itemId, event) => {
    event.stopPropagation();
    setOpenSubMenus(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const isActive = (path) => {
    if (path.includes('?')) {
      const pathWithoutQuery = path.split('?')[0];
      return location.pathname === pathWithoutQuery;
    }
    return location.pathname === path;
  };

  return (
    <>
      <Box
        sx={{
          width: expanded ? '240px' : '60px',
          backgroundColor: theme.palette.primary.dark,
          color: 'white',
          height: '100vh',
          transition: 'all 0.3s ease',
          overflow: 'hidden',
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: theme.zIndex.drawer + 1,
          boxShadow: expanded ? '4px 0 20px rgba(0, 0, 0, 0.15)' : '0 0 10px rgba(0, 0, 0, 0.2)',
          borderRight: `1px solid ${theme.palette.divider}`,
        }}
      >
        <IconButton
          size="small"
          onClick={toggleExpand}
          sx={{
            position: 'absolute',
            top: 12,
            right: 8,
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
            padding: 4,
            zIndex: 1200,
          }}
        >
          {expanded ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>

        <NavList>
          {items.map((item) => {
            const active = isActive(item.path);
            const hasSubMenu = item.children && item.children.length > 0;
            const subMenuOpen = openSubMenus[item.id];
            const accessible = isItemAccessible(item);

            const navItemContent = (
              <NavItem
                component={'div'}
                onClick={(e) => {
                  if (!accessible) return;
                  if (hasSubMenu && expanded) {
                    toggleSubMenu(item.id, e);
                  } else if (!hasSubMenu) {
                    handleNavItemClick(item);
                  }
                }}
                active={active ? 1 : 0}
                expanded={expanded ? 1 : 0}
                highlight={item.highlight ? 1 : 0}
                sx={{
                  opacity: accessible ? 1 : 0.5,
                  cursor: accessible ? 'pointer' : 'not-allowed',
                }}
              >
                <ListItemIcon sx={{ minWidth: expanded ? 36 : 'auto', color: accessible ? (active ? 'navActive.main' : 'inherit') : 'rgba(255, 255, 255, 0.3)' }}>
                  {item.icon}
                </ListItemIcon>
                {expanded && (
                  <>
                    <NavItemText primary={item.label} />
                    {item.comingSoon && <Chip label="Soon" size="small" sx={{ ml: 'auto', height: 18, fontSize: '0.6rem', bgcolor: 'rgba(255,180,0,0.25)', color: '#ffb400', fontWeight: 700 }} />}
                    {!accessible && <LockIcon fontSize="small" sx={{ ml: 'auto', opacity: 0.5, fontSize: '1rem' }} />}
                  </>
                )}
                {expanded && hasSubMenu && accessible && (
                  <IconButton size="small" onClick={(e) => toggleSubMenu(item.id, e)} sx={{ color: 'inherit', p: 0.5 }}>
                    {subMenuOpen ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                  </IconButton>
                )}
              </NavItem>
            );

            return (
              <Box key={item.id}>
                {!accessible && expanded && item.requiresRole ? (
                  <Tooltip title={`Requires ${item.requiresRole.replace('_', ' ')} access`} placement="right" arrow>
                    {navItemContent}
                  </Tooltip>
                ) : (
                  navItemContent
                )}

                {expanded && hasSubMenu && (
                  <SubMenu in={subMenuOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.children.map((child) => {
                        const childActive = isActive(child.path);
                        return (
                          <SubMenuItem
                            key={child.id}
                            component={child.needsSelection ? 'div' : Link}
                            to={child.needsSelection ? undefined : child.path}
                            active={childActive ? 1 : 0}
                            onClick={() => child.needsSelection ? handleNavItemClick(child) : handleSubMenuItemClick(child.path)}
                            sx={{
                              cursor: 'pointer'
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, color: childActive ? 'navActive.main' : 'inherit' }}>
                              {child.icon}
                            </ListItemIcon>
                            <NavItemText primary={child.label} />
                            {child.comingSoon && <Chip label="Soon" size="small" sx={{ ml: 'auto', height: 18, fontSize: '0.6rem', bgcolor: 'rgba(255,180,0,0.25)', color: '#ffb400', fontWeight: 700 }} />}
                          </SubMenuItem>
                        );
                      })}
                    </List>
                  </SubMenu>
                )}
              </Box>
            );
          })}
        </NavList>

        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, borderTop: '1px solid rgba(255, 255, 255, 0.1)', backgroundColor: 'rgba(0, 0, 0, 0.2)' }}>
          {expanded && profile && (
            <>
              {/* Clinic Selector for Super Admins */}
              {(isAdmin || isProvider) && (
                <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                  <ClinicSelector
                    onSelection={() => {
                      if (expanded) toggleExpand();
                    }}
                    sx={{
                      color: 'white',
                      '& .MuiSelect-select': { color: 'white' },
                      '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.7)' },
                      '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, 0.7)' }
                    }}
                  />
                </Box>
              )}
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.secondary.main, fontSize: '0.875rem' }}>
                    {profile.first_name?.[0]}{profile.last_name?.[0]}
                  </Avatar>
                  <Box sx={{ ml: 1, flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {profile.first_name} {profile.last_name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                      {profile.tenant_role?.replace('_', ' ')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
            </>
          )}
          <Box sx={{ display: 'flex', justifyContent: expanded ? 'space-around' : 'center', p: 1 }}>
            {expanded && refreshProfile && (
              <Tooltip title="Refresh Profile" arrow>
                <IconButton size="small" onClick={() => refreshProfile()} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' } }}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={expanded ? "Logout" : "Logout"} arrow placement="right">
              <IconButton
                size="small"
                onClick={async () => {
                  try {
                    await logout();
                    // Navigate immediately after signout
                    // Use replace to prevent back button from going to protected route
                    navigate('/login', { replace: true });
                  } catch (error) {
                    console.error('Error during logout:', error);
                    // Even if logout fails, navigate to login
                    navigate('/login', { replace: true });
                  }
                }}
                sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' } }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      <UserSelectorModal
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        userType={selectorUserType}
        onSelection={() => {
          if (expanded) toggleExpand();
        }}
      />
    </>
  );
};

export default SideNav;
